<script setup lang="ts">
import { inject } from 'vue'

import { AccordionContextSymbol } from '@/components/Accordion/context'
import { useSidebarStore } from '~~/src/stores/sidebar'
import { useElementalListsStore } from '~~/src/stores/useElementalListsStore'
import type { List } from '~~/src/types/list'
import type { Elemental } from '~~/src/types/elemental'
import { ElementalTypeEnum } from '~~/src/types/elemental'
import { Button } from '@/components/ui/button'
import ElementalMenu from '@/components/Elemental/Options/index.vue'
import { useCreateElemental } from '~~/src/composables/mutations/elemetal/useCreateElemental'
import { useSaveElementalInAList } from '~~/src/composables/mutations/elemetal/useSaveElementalInAList'
import ElementProvider from '~~/src/components/Elemental/context/ElementProvider.vue'
import Share from '@/components/Elemental/Actions/Share/index.vue'
import ElementalListMenu from '@/components/ElemetalMenu/index.vue'
import GroupedActions from '@/components/Library/TableLibrary/components/GroupedActions/index.vue'

const props = defineProps<{
  isMain?: boolean
  title?: string
  nPrompts?: number
  iconBoxClass?: string
  listId: string
  parentListId?: string | number
  list?: List | Elemental
  elementalsType: ElementalTypeEnum
}>()
const router = useRouter()
const toast = useToast()
const { toggleElementOpen } = useSidebarStore()
const user = useUser()
const isHover = ref(false)
const elementalCreationLoading = ref(false)
const { mutate: saveElementalInAListMutate } = useSaveElementalInAList()

const accordeonContext = inject(AccordionContextSymbol)

if (!accordeonContext)
  throw new Error('Accordeon context not found')

const { isOpen, toggle } = accordeonContext

watch(isOpen, (value) => {
  if (props.listId)
    toggleElementOpen(props.listId, props.parentListId ? `${props.parentListId}` : null, value)
})
const { mutate: createElemental, isPending } = useCreateElemental()
const { toggleSidebar } = useSidebarStore()
const { favorite } = useFavorite(props.list as Elemental)
const isOwner = computed(() => user.value?.id === props.list?.user.id)
const isFavorite = ref(props.list?.isFavorite ?? false)

const elementalRoute = computed(() => {
  const route = getElementalTypeRoute(props.list?.elementalTypeId ?? ElementalTypeEnum.List)

  if (isOwner.value)
    return (`/${route}/${props.list?.id}/edit`)
  else
    return (`/${route}/${props.list?.url}`)
})

watch(() => props.list?.isFavorite, (newValue) => {
  isFavorite.value = newValue ?? false
})

const handlerToggle = () => {
  toggle()
}

const handlerCreateElemental = (event: Event) => {
  event.stopPropagation()

  isHover.value = true
  elementalCreationLoading.value = true

  if (!user.value)
    return router.push(useGetLoginURL())
  const elementalType = props.list?.contentTypeId === ElementalTypeEnum.List ? ElementalTypeEnum.Document : props.list?.contentTypeId

  if (!elementalType)
    return

  const route = getElementalTypeRoute(elementalType)

  const elementalData = elementalFactory(elementalType, {
    title: 'Untitled',
  })

  createElemental({ elementalType, elemental: elementalData }, {
    onSuccess: async (result) => {
      if (!result?.id) {
        toast.error('Error creating item')
        return
      }

      saveElementalInAListMutate({
        id: result?.id,
        elementalType: props.list?.contentTypeId as ElementalTypeEnum,
        listId: props.listId,
        orderIndex: 0,
      }, {
        onSuccess: () => {
          router.push(`/${route}/${result?.id}/edit`)
          elementalCreationLoading.value = false
        },
        onError: (error) => {
          toast.error(error.message)
        },
      })
    },
    onError: () => {
      elementalCreationLoading.value = false
    },
  })
}

const toggleMenu = () => {
  isHover.value = !isHover.value
}

const handlerFavorite = (event: Event) => {
  event.stopPropagation()
  favorite(isFavorite.value)
  isFavorite.value = !isFavorite.value
}

const expandElemental = (event: Event) => {
  event.stopPropagation()
  toggleSidebar(false)
  setTimeout(() => {
    router.push(elementalRoute.value)
  }, 200)
}

const { state } = useElementalListsStore()
</script>

<template>
  <button
    v-if="!isMain"
    class="flex items-center w-full  rounded-lg group"
    :class="$style.wrapper"
    @click="handlerToggle()"
  >
    <div class="drag-image w-full" :class="[$style['drag-view']]">
      <slot name="header" />
    </div>

    <BubbleCount
      v-if="isMain"
    >
      {{ nPrompts }}
    </BubbleCount>

    <span
      :class="[$style.wrapper_actions]" flex items-center justify-self-end ml-a
    >
      <div :class="$style.actions">
        <GroupedActions
          v-if="!isMain"
          :is-hover="isHover"
          class="p-1 group-hover:opacity-100"
        >
          <template #actions>
            <ElementProvider
              :elemental="list"
              :type="list?.contentTypeId ?? ElementalTypeEnum.List"
              :is-owner="isOwner"
            >
              <Share class="!w-8 !h-8" icon-class="!w-4 !h-4" btn-variant="snack-icon-cube-rounded" />
            </ElementProvider>
            <Button
              variant="snack-icon-cube-rounded"
              size="icon"
              icon="sm"
              class=" text-soft-black font-700 text-xs w-8 h-8"
              @click="handlerFavorite"
            >
              <div v-if="isFavorite" i-mdi:star class="w-4 h-4" />
              <div v-else i-mdi:star-outline class="w-4 h-4" />
            </Button>
            <Button
              variant="snack-icon-cube-rounded"
              size="icon"
              icon="sm"
              class=" text-soft-black font-700 text-xs w-8 h-8"
              :disabled="elementalCreationLoading"
              @click="(event) => handlerCreateElemental(event)"
            >
              <div v-if="!elementalCreationLoading" class="i-material-symbols:add w-4 h-4" />
              <div v-else class="i-mdi:loading animate-spin w-4 h-4" />
            </Button>
            <Button
              v-if="isOwner"
              variant="snack-icon-cube-rounded"
              size="icon"
              icon="sm"
              class=" text-soft-black font-700 text-xs w-8 h-8"
              @click="(event) => expandElemental(event)"
            >
              <IconArrowExpand class="w-4 h-4" />
            </Button>
            <ElementalMenu
              v-if="list"
              :elementals-type="list?.contentTypeId"
              :elemental="list"
              :parent-list-id="parentListId"
              btn-class="w-8 h-8 [&_svg]:w-4 [&_svg]:h-4"
              btn-variant="snack-icon-cube-rounded"
              @open-menu="toggleMenu"
            />
          </template>
        </GroupedActions>
        <div
          i-material-symbols:keyboard-arrow-down
          :rotate="isOpen ? 360 : 270"
          :class="$style.arrow_icon"
        />
      </div>
    </span>
  </button>
  <button
    v-else
    class="flex items-center w-full  rounded-lg "
    :class="[$style.wrapper]"
    @click="handlerToggle()"
  >
    <div class="flex gap-1.5 w-full">
      <slot name="header" />
    </div>

    <!-- <BubbleCount
      v-if="isMain"
    >
      {{ nPrompts }}
    </BubbleCount> -->

    <!-- absolute aqui -->
    <span :class="$style.wrapper_actions" flex items-center justify-self-end ml-a>

      <div :class="$style.actions">
        <GroupedActions
          v-if="!isMain"
          :is-hover="isHover"
          class="group-hover:opacity-100"
        >
          <Button
            v-if="isOwner"
            variant="snack-icon-cube-rounded"
            size="icon"
            icon="sm"
            class=" text-soft-black font-700 text-xs"
            @click="expandElemental"
          >
            <IconArrowExpand class="w-4 h-4" />
          </Button>
          <template #actions>
            <ElementalListMenu v-if="list" :elemental="list" :elementals-type="elementalsType" is-list-element />
          </template>
        </GroupedActions>
        <span
          flex items-center justify-self-end ml-a mr-1 w-6
          h-6
        />
        <div
          i-material-symbols:keyboard-arrow-down
          :class="$style.arrow_icon"
          :rotate="isOpen ? 360 : 270"
        />
      </div>
    </span>
  </button>
</template>

<style module lang="scss">
    .wrapper_actions {
      position: absolute;
      right: 8px;
      pointer-events: none;
    }

    .arrow_icon {
      @apply w-9 h-9 text-dark-gray;
      transition: transform 0.3s ease-in-out;
      pointer-events: none !important;
    }

    .wrapper {
    @apply w-auto
    w-full
    flex
    flex-nowrap
    justify-left
    gap-2
    items-center
    relative;

    &.active,
    &:hover {
    .actions {
        .actions__hidden {
          @apply opacity-100;
        }
      }
    }

    .actions {
      @apply flex
      gap-.5
      justify-self-end
      ml-auto
      z-10
      items-center;
      pointer-events: none;

      .actions__hidden {
        pointer-events: auto;
        background-color: #fff;
      border-radius: 8px;
      border: 1px solid #E3E9F8;
      box-shadow: 0px 0px 6px 0px rgba(0, 0, 0, 0.05);
      }
    }
    .actions__hidden {

      @apply opacity-0 items-center transition-opacity duration-300;
    }
    }
  </style>
